import { onBeforeMount } from 'vue';
import { pushReportUrl } from '@/views/reports/report-route-push';
import { runReportFiltersBeforeMount } from '@/views/reports/report-filters-lifecycle';
import { isDedicatedReportRoute } from '@/views/reports/report-route-behavior';

export const createInitialSortingFilters = () => ({
  descending: true,
  totalItems: 50,
  rowsPerPage: 50,
  page: 1,
});

export const applyReportSortingFilters = (
  sortingFilters: {
    sortBy?: unknown;
    descending?: boolean;
  },
  routeName: string | symbol | null | undefined,
  getFilters: () => Record<string, unknown>,
  getReportParams: () => Record<string, unknown> | undefined,
) => {
  if (isDedicatedReportRoute(routeName)) {
    const reportParams = getReportParams() || {};
    sortingFilters.sortBy = reportParams.orderBy;
    sortingFilters.descending = reportParams.orderDir;
    return;
  }

  const filters = getFilters();
  sortingFilters.sortBy = filters.orderBy;
  sortingFilters.descending = filters.orderDir === 'desc';
};

export type ReportFiltersSortingState = ReturnType<typeof createInitialSortingFilters> & {
  sortBy?: unknown;
};

export type ReportFiltersRuntimeContext = {
  isDashboard: boolean;
  subReport: boolean;
  filterPromise: Promise<unknown>;
  clearFilter: () => void;
  getRouteName: () => string | symbol | null | undefined;
  getFilters: () => Record<string, unknown>;
  getReportParams: () => Record<string, unknown> | undefined;
  onFiltersActive: () => void;
};

export const createReportFiltersSortingState = () => {
  return createInitialSortingFilters() as ReportFiltersSortingState;
};

export const createReportFiltersHandlers = (
  context: ReportFiltersRuntimeContext,
  sortingFilters: ReportFiltersSortingState,
) => {
  const setSorting = () => {
    applyReportSortingFilters(
      sortingFilters,
      context.getRouteName(),
      context.getFilters,
      context.getReportParams,
    );
  };

  return {
    setSorting,
    beforeMount: () => {
      runReportFiltersBeforeMount({
        isDashboard: context.isDashboard,
        subReport: context.subReport,
        filterPromise: context.filterPromise,
        clearFilter: context.clearFilter,
        setSorting,
        onFiltersActive: context.onFiltersActive,
      });
    },
    pushURL: (pushContext: Parameters<typeof pushReportUrl>[1]) => {
      return pushReportUrl(context.getRouteName(), pushContext);
    },
  };
};

export const useReportFiltersSorting = (context: ReportFiltersRuntimeContext) => {
  const sortingFilters = createReportFiltersSortingState();
  const handlers = createReportFiltersHandlers(context, sortingFilters);

  onBeforeMount(() => {
    handlers.beforeMount();
  });

  return {
    sortingFilters,
    setSorting: handlers.setSorting,
    pushURL: handlers.pushURL,
  };
};
